import { useState } from "react"
import { Link } from "react-router-dom"
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa"
import { FaFileCirclePlus } from "react-icons/fa6"
import Select from 'react-select'
import Button from '../../../shared/components/Button/Button'

/* form */
import Input from "../../../shared/components/form/input"
import CepSearchTrigger from "../../../shared/components/CepSearchTrigger/CepSearchTrigger"
import CnpjSearchTrigger from "../../../shared/components/CnpjSearchTrigger/CnpjSearchTrigger"
import Radio from "../../../shared/components/form/Radio"
import Checkbox from "../../../shared/components/form/checkbox"
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import FormSection from "../../../shared/components/FormLayout/FormSection"
import RequiredMark from "../../../shared/components/form/RequiredMark"
import Modal from "../../../shared/modal/Modal"
import ConfirmDeleteModal from "../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal"
import useFlashMessage from "../../../hooks/useFlashMessage"
import SelectStyles from "../../../shared/Select/SelectStyle"
import { maskPhone, maskCPF, maskCNPJ, maskCEP, normalizePhoneDigits, normalizeCPFDigits, normalizeCNPJDigits, normalizeCEPDigits } from "../../../utils/formatters"

import "../../../shared/css/subItemList.css"
import "./FormClient.css"

// campos que só aceitam números: o state guarda só os dígitos e a máscara é aplicada na exibição
const MASKED_FIELDS = {
    phone: { mask: maskPhone, normalize: normalizePhoneDigits },
    CPF: { mask: maskCPF, normalize: normalizeCPFDigits },
    CNPJ: { mask: maskCNPJ, normalize: normalizeCNPJDigits },
    zipCode: { mask: maskCEP, normalize: normalizeCEPDigits },
}

const IE_INDICATOR_OPTIONS = [
    { value: 'contribuinte', label: 'Contribuinte de ICMS' },
    { value: 'isento', label: 'Contribuinte isento de IE' },
    { value: 'nao_contribuinte', label: 'Não contribuinte' },
]

const EMPTY_CONTACT_DRAFT = { name: '', type: 'phone', phone: '', email: '' }

// Clientes antigos só têm o par contact/phone — mostra como o primeiro contato da lista.
function initialClient(clientData) {
    if (!clientData) return { personType: "fisica", contacts: [] }
    if (Array.isArray(clientData.contacts) && clientData.contacts.length > 0) return clientData
    const legacy = (clientData.contact || clientData.phone) ? [{ name: clientData.contact || '', type: 'phone', phone: clientData.phone || '' }] : []
    return { ...clientData, contacts: legacy }
}

const BRAZIL_STATES = [
    { label: 'AC', value: 'AC' }, { label: 'AL', value: 'AL' }, { label: 'AP', value: 'AP' },
    { label: 'AM', value: 'AM' }, { label: 'BA', value: 'BA' }, { label: 'CE', value: 'CE' },
    { label: 'DF', value: 'DF' }, { label: 'ES', value: 'ES' }, { label: 'GO', value: 'GO' },
    { label: 'MA', value: 'MA' }, { label: 'MT', value: 'MT' }, { label: 'MS', value: 'MS' },
    { label: 'MG', value: 'MG' }, { label: 'PA', value: 'PA' }, { label: 'PB', value: 'PB' },
    { label: 'PR', value: 'PR' }, { label: 'PE', value: 'PE' }, { label: 'PI', value: 'PI' },
    { label: 'RJ', value: 'RJ' }, { label: 'RN', value: 'RN' }, { label: 'RS', value: 'RS' },
    { label: 'RO', value: 'RO' }, { label: 'RR', value: 'RR' }, { label: 'SC', value: 'SC' },
    { label: 'SP', value: 'SP' }, { label: 'SE', value: 'SE' }, { label: 'TO', value: 'TO' },
]

function FormClient({ handleSubmit, clientData, btnText, btCancelLink, btCancelAction, title, sellerEnabled, sellerData }) {

    const { setFlashMessage } = useFlashMessage()
    const [client, setClient] = useState(() => initialClient(clientData))

    const [modalContact, setModalContact] = useState(false)
    const [editingContactIndex, setEditingContactIndex] = useState(null)
    const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT_DRAFT)
    const [modalDeleteContact, setModalDeleteContact] = useState(false)
    const [deleteContactIndex, setDeleteContactIndex] = useState(null)

    const contacts = client.contacts || []

    function handleChange(e) {
        setClient({ ...client, [e.target.name]: e.target.value })
    }

    function handleChangeMasked(e) {
        const { name, value } = e.target
        const { mask, normalize } = MASKED_FIELDS[name]
        let digits = normalize(value)
        // apagou só um caractere da máscara (parêntese, espaço, hífen, ponto): remove o último dígito, senão o backspace "trava"
        if (digits === normalize(client[name]) && value.length < mask(client[name]).length) {
            digits = digits.slice(0, -1)
        }
        setClient({ ...client, [name]: digits })
    }

    // Preenche os dados da empresa vindos da Receita; campo que a Receita não devolve fica como estava.
    function handleCnpjFound(company) {
        setClient((prev) => ({
            ...prev,
            corporateName: company.corporateName || prev.corporateName,
            fantasyName: company.fantasyName || prev.fantasyName,
            logradouro: company.address || prev.logradouro,
            number: company.number || prev.number,
            complement: company.complement || prev.complement,
            district: company.district || prev.district,
            city: company.city || prev.city,
            state: company.state || prev.state,
            zipCode: company.cep || prev.zipCode,
            cityCode: company.cityCode || prev.cityCode,
            email: company.email || prev.email,
        }))
    }

    // Endereço vindo do ViaCEP; número, complemento e o resto continuam como estavam.
    function handleCepFound(address) {
        setClient((prev) => ({
            ...prev,
            logradouro: address.street || prev.logradouro,
            district: address.district || prev.district,
            city: address.city || prev.city,
            state: address.state || prev.state,
            cityCode: address.cityCode || prev.cityCode,
        }))
    }

    const handleChangeRadio = (name, value) => {
        setClient({ ...client, [name]: value })
    }

    function handleChangeCheckbox(name, value) {
        setClient({ ...client, [name]: value })
    }

    function handleChangeState(selectedOption) {
        setClient({ ...client, state: selectedOption.value })
    }

    function openAddContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(true)
    }

    function openEditContactModal(idx) {
        // contatos antigos não tinham "type" (só nome + telefone) — trata como telefone
        setContactDraft({ type: 'phone', ...contacts[idx] })
        setEditingContactIndex(idx)
        setModalContact(true)
    }

    function closeContactModal() {
        setContactDraft(EMPTY_CONTACT_DRAFT)
        setEditingContactIndex(null)
        setModalContact(false)
    }

    function handleContactDraftChange(e) {
        const { name, value } = e.target
        if (name === 'phone') {
            let digits = normalizePhoneDigits(value)
            // apagou só um caractere da máscara: remove o último dígito, senão o backspace "trava"
            if (digits === normalizePhoneDigits(contactDraft.phone) && value.length < maskPhone(contactDraft.phone).length) {
                digits = digits.slice(0, -1)
            }
            setContactDraft({ ...contactDraft, phone: digits })
            return
        }
        setContactDraft({ ...contactDraft, [name]: value })
    }

    function handleContactTypeChange(name, value) {
        setContactDraft({ ...contactDraft, [name]: value })
    }

    function saveContactDraft(e) {
        // botão comum (não submit) — ver comentário no FormLayout do modal de contato
        e.preventDefault()
        e.stopPropagation()

        if (!contactDraft.name) {
            setFlashMessage('Digite o nome do contato', 'error')
            return
        }
        if (contactDraft.type === 'email' ? !contactDraft.email : !contactDraft.phone) {
            setFlashMessage(contactDraft.type === 'email' ? 'Digite o email do contato' : 'Digite o telefone do contato', 'error')
            return
        }

        // só guarda o campo do tipo escolhido — o outro não se aplica a esse contato
        const toSave = contactDraft.type === 'email'
            ? { name: contactDraft.name, type: 'email', email: contactDraft.email, phone: '' }
            : { name: contactDraft.name, type: 'phone', phone: contactDraft.phone, email: '' }

        const rows = editingContactIndex !== null
            ? contacts.map((c, i) => (i === editingContactIndex ? toSave : c))
            : [...contacts, toSave]
        setClient({ ...client, contacts: rows })
        closeContactModal()
    }

    function askRemoveContact(idx) {
        setDeleteContactIndex(idx)
        setModalDeleteContact(true)
    }

    function confirmRemoveContact() {
        setClient({ ...client, contacts: contacts.filter((_, i) => i !== deleteContactIndex) })
        setDeleteContactIndex(null)
        setModalDeleteContact(false)
    }

    function handleChangeIeIndicator(selectedOption) {
        setClient({ ...client, ieIndicator: selectedOption ? selectedOption.value : '' })
    }

    function openInMaps() {
        const parts = [client.logradouro, client.number, client.district, client.city, client.state, client.zipCode].filter(Boolean)
        const query = encodeURIComponent(parts.join(', '))
        window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank', 'noopener,noreferrer')
    }

    function submit(e) {
        e.preventDefault()
        handleSubmit(client)
    }

    return (
        <FormLayout
            title={title}
            onSubmit={submit}
            requiredLegend
            actions={
                <>
                    <Button variant="save" type="submit">{btnText || 'Salvar'}</Button>
                    {btCancelLink
                        ? <Link className="link-default" to={btCancelLink}><Button variant="cancel">Cancelar</Button></Link>
                        : btCancelAction && <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                    }
                </>
            }
        >
            {/* ── Identificação ── */}
            <FormSection label="Identificação">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            text="Cliente"
                            required
                            type="text"
                            name="name"
                            placeholder="Digite o nome do cliente"
                            handleOnChange={handleChange}
                            value={client.name || ''}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <Checkbox
                        text="Cliente desabilitado"
                        name="disabled"
                        handleOnChange={() => handleChangeCheckbox("disabled", !client.disabled)}
                        check={client.disabled || false}
                    />
                </div>
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <label className="input-label">Tipo de pessoa:<RequiredMark /></label>
                        <Radio
                            options={[
                                { value: 'fisica', label: "Pessoa física" },
                                { value: 'juridica', label: "Pessoa jurídica" }
                            ]}
                            name={"personType"}
                            value={client.personType}
                            defaultValue="fisica"
                            handleOnChange={handleChangeRadio}
                        />
                    </div>
                </div>
            </FormSection>

            {/* ── Dados da Pessoa Jurídica ── */}
            {client.personType === "juridica" && (
                <FormSection label="Dados da Pessoa Jurídica">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input required text="Razão Social" type="text" name="corporateName" placeholder="Digite a razão social" handleOnChange={handleChange} value={client.corporateName || ''} />
                        </div>
                        <div className="fl-col-mid form-group">
                            <Input required text="Nome Fantasia" type="text" name="fantasyName" handleOnChange={handleChange} value={client.fantasyName || ''} />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <CnpjSearchTrigger required text="CNPJ" name="CNPJ" placeholder="00.000.000/0000-00" handleOnChange={handleChangeMasked} value={maskCNPJ(client.CNPJ)} onFound={handleCnpjFound} />
                        </div>
                    </div>
                </FormSection>
            )}

            {/* ── Dados da Pessoa Física ── */}
            {client.personType === "fisica" && (
                <FormSection label="Dados da Pessoa Física">
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <Input required text="Nome Completo" type="text" name="completeName" placeholder="Digite o nome completo" handleOnChange={handleChange} value={client.completeName || ''} />
                        </div>
                        <div className="fl-col-narrow form-group">
                            <Input required text="CPF" type="text" name="CPF" placeholder="000.000.000-00" handleOnChange={handleChangeMasked} value={maskCPF(client.CPF)} />
                        </div>
                    </div>
                </FormSection>
            )}

            {/* ── Endereço ── */}
            <FormSection label="Endereço">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input required text="Logradouro" type="text" name="logradouro" handleOnChange={handleChange} value={client.logradouro || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input required text="Bairro" type="text" name="district" handleOnChange={handleChange} value={client.district || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input required text="Número" type="text" name="number" handleOnChange={handleChange} value={client.number || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Complemento" type="text" name="complement" handleOnChange={handleChange} value={client.complement || ''} />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input required text="Cidade" type="text" name="city" handleOnChange={handleChange} value={client.city || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <label className="input-label">UF:<RequiredMark /></label>
                        <Select
                            options={BRAZIL_STATES}
                            onChange={handleChangeState}
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            value={BRAZIL_STATES.find(s => s.value === client.state) || null}
                            placeholder="UF"
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <CepSearchTrigger required text="CEP" name="zipCode" placeholder="00000-000" handleOnChange={handleChangeMasked} value={maskCEP(client.zipCode)} onFound={handleCepFound} />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Button variant="map" type="button" onClick={openInMaps} disabled={!client.logradouro && !client.city}>
                            Ver no mapa
                        </Button>
                    </div>
                </div>
            </FormSection>

            {/* ── Contato ── */}
            <FormSection
                label="Contato"
                actions={
                    <button type="button" className="cl-add-contact-btn" title="Adicionar contato" onClick={openAddContactModal}>
                        <FaFileCirclePlus size={23} />
                    </button>
                }
            >
                <div className="sil-list">
                    {contacts.length === 0 && (
                        <span className="sil-empty">Nenhum contato cadastrado — clique no ícone acima para adicionar.</span>
                    )}
                    {contacts.map((row, idx) => (
                        <div key={idx} className="sil-row" title="Clique para editar" onClick={() => openEditContactModal(idx)}>
                            <span className="sil-label">{row.name}</span>
                            <span className="sil-value">{row.type === 'email' ? row.email : maskPhone(row.phone)}</span>
                            <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={(e) => { e.stopPropagation(); openEditContactModal(idx) }} />
                            <FaTrashAlt className="shd-icon-row-trash" title="Remover" onClick={(e) => { e.stopPropagation(); askRemoveContact(idx) }} />
                        </div>
                    ))}
                </div>

                <Modal
                    modalStatus={modalContact}
                    changeStatus={setModalContact}
                    w={45}
                    content={
                        // Sem onSubmit: fica <div>, não <form> — evita form aninhado dentro do
                        // form do cliente (o Modal não é portal de verdade; ver FormClient.css/Modal.js).
                        // "Adicionar/Salvar" é um botão comum, não um submit.
                        <FormLayout
                            title={editingContactIndex !== null ? "Editar Contato" : "Adicionar Contato"}
                            requiredLegend
                            actions={
                                <>
                                    <Button variant="finish" type="button" onClick={saveContactDraft}>{editingContactIndex !== null ? "Salvar" : "Adicionar"}</Button>
                                    <Button variant="cancel" type="button" onClick={closeContactModal}>Cancelar</Button>
                                </>
                            }
                        >
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    <Input required text="Nome" type="text" name="name" placeholder="Nome do contato" handleOnChange={handleContactDraftChange} value={contactDraft.name || ''} />
                                </div>
                            </div>
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    <label className="input-label">Tipo de contato:<RequiredMark /></label>
                                    <Radio
                                        options={[
                                            { value: 'phone', label: 'Telefone' },
                                            { value: 'email', label: 'Email' },
                                        ]}
                                        name="type"
                                        value={contactDraft.type}
                                        defaultValue="phone"
                                        handleOnChange={handleContactTypeChange}
                                    />
                                </div>
                            </div>
                            <div className="fl-row">
                                <div className="fl-col-wide form-group">
                                    {contactDraft.type === 'email' ? (
                                        <Input required text="Email" type="email" name="email" placeholder="contato@exemplo.com" handleOnChange={handleContactDraftChange} value={contactDraft.email || ''} />
                                    ) : (
                                        <Input required text="Telefone" type="tel" name="phone" placeholder="(00) 00000-0000" handleOnChange={handleContactDraftChange} value={maskPhone(contactDraft.phone)} />
                                    )}
                                </div>
                            </div>
                        </FormLayout>
                    }
                />

                <ConfirmDeleteModal
                    modalStatus={modalDeleteContact}
                    changeStatus={setModalDeleteContact}
                    onConfirm={confirmRemoveContact}
                    message="Deseja realmente remover esse contato?"
                    itemName={deleteContactIndex !== null ? contacts[deleteContactIndex]?.name : ''}
                />
            </FormSection>

            {/* ── Dados Fiscais ── */}
            <FormSection label="Dados Fiscais">
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input text="Inscrição Estadual" type="text" name="stateRegistration" placeholder="Opcional" handleOnChange={handleChange} value={client.stateRegistration || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <label className="input-label">Indicador de IE:</label>
                        <Select
                            options={IE_INDICATOR_OPTIONS}
                            onChange={handleChangeIeIndicator}
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            value={IE_INDICATOR_OPTIONS.find(o => o.value === client.ieIndicator) || null}
                            placeholder="Selecione"
                            isClearable
                        />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="Cód. IBGE do município" type="text" name="cityCode" placeholder="Preenchido pelo CEP/CNPJ" handleOnChange={handleChange} value={client.cityCode || ''} />
                    </div>
                </div>
            </FormSection>

            {/* ── Observações ── */}
            <FormSection label="Observações">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input text="Obs" type="text" name="obs" handleOnChange={handleChange} value={client.obs || ''} />
                    </div>
                </div>
            </FormSection>

        </FormLayout>
    )
}

export default FormClient
